// Kiosk plumbing that is not UI: shift events, the heartbeat, the "here today" list and the
// small records the kiosk keeps. Everything persistent lives in the client's Dexie DB
// (db.meta), never in localStorage, so a wipe or a revoke leaves nothing behind.
import { pendingCount, type DeviceApi, type OutboxEntry, type VavrDb } from "@vavr/client";
import { uuidv7, type PreImage } from "@vavr/core";
import { percentile } from "./metrics.ts";

export const SHIFT_MAX_MS = 12 * 3600_000;
export const ROSTER_EVERY_MS = 60_000;
export const STATUS_EVERY_MS = 5 * 60_000;
export const HEARTBEAT_EVERY_MS = 5 * 60_000;

export interface Shift { staff_id: string; name: string; started_at: number }

export async function getMeta<T>(db: VavrDb, key: string): Promise<T | null> {
  return ((await db.meta.get(key))?.value as T | undefined) ?? null;
}
export async function setMeta(db: VavrDb, key: string, value: unknown) {
  await db.meta.put({ key, value });
}

/** A shift event has no child, adult or attestation (events spec §2; validatePreImage allows it). */
export function shiftEvent(type: "shift_start" | "shift_end", o: { siteId: string; deviceId: string; staffId: string; monoStart: number; now?: number }): PreImage {
  const now = o.now ?? Date.now();
  return {
    v: 1, id: uuidv7(now), site_id: o.siteId, type, group_id: null, child_id: null, adult_id: null, claimed_name: null,
    id_check: null, staff_id: o.staffId, witness_staff_id: null, device_id: o.deviceId, device_ts: now,
    device_mono: Math.max(0, Math.round(now - o.monoStart)), photo_sha256: null, face: null, stroke_sha256: null,
    attestation: null, auth_method: "none", flags: [], reason: null, data: null,
  };
}

export const shiftExpired = (s: Shift | null, now = Date.now()) => Boolean(s && now - s.started_at >= SHIFT_MAX_MS);

export function startOfDay(now = Date.now()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Children handled on this device today, most recent first. For pickups: children dropped off
 * and not yet collected ("here today"); for drop-offs: nobody is preferred, so the list is empty.
 */
export function todayList(entries: Pick<OutboxEntry, "pre" | "created_at">[], direction: "pickup" | "dropoff"): string[] {
  if (direction !== "pickup") return [];
  const last = new Map<string, { type: string; at: number }>();
  for (const e of [...entries].sort((a, b) => a.created_at - b.created_at)) {
    if (!e.pre.child_id || (e.pre.type !== "pickup" && e.pre.type !== "dropoff")) continue;
    last.set(e.pre.child_id, { type: e.pre.type, at: e.created_at });
  }
  return [...last.entries()].filter(([, v]) => v.type === "dropoff").sort((a, b) => b[1].at - a[1].at).map(([id]) => id);
}

interface BatteryLike { level: number }

/** `appVersion`: the build's own version (the Android app's, or "web"), shown per device in the console. */
export async function heartbeatBody(db: VavrDb, samples: number[], appVersion: string) {
  let battery: number | null = null;
  try {
    const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryLike> };
    if (nav.getBattery) battery = Math.round((await nav.getBattery()).level * 100);
  } catch { /* not available */ }
  return { battery, outbox_depth: await pendingCount(db), app_version: appVersion, tap_to_done_ms_p95: percentile(samples, 95) };
}

export async function sendHeartbeat(api: DeviceApi, db: VavrDb, samples: number[], appVersion: string) {
  const r = await api.json<{ ok: boolean; status: string; server_now: number }>("POST", "/api/v1/device/heartbeat", { json: await heartbeatBody(db, samples, appVersion) });
  api.skewMs = r.server_now - Date.now();
  return r;
}
